import React, { useState, useEffect, useCallback } from 'react';
import { Images, Upload, FolderOpen, Plus, X, Grid3X3, LayoutGrid, Columns, GalleryHorizontal, Maximize2, Trash2, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Switch } from './ui/switch';
import api from '../utils/api';
import { toast } from 'sonner';

const GALLERY_LAYOUTS = [
  { id: 'grid-2', name: 'Grid 2 Columns', icon: Grid3X3, columns: 2 },
  { id: 'grid-3', name: 'Grid 3 Columns', icon: Grid3X3, columns: 3 },
  { id: 'grid-4', name: 'Grid 4 Columns', icon: LayoutGrid, columns: 4 },
  { id: 'masonry', name: 'Masonry', icon: Columns, description: 'Pinterest-style layout' },
  { id: 'carousel', name: 'Carousel', icon: GalleryHorizontal, description: 'Horizontal slider' },
  { id: 'featured', name: 'Featured + Thumbnails', icon: Maximize2, description: 'Large image with thumbnails' },
];

const GalleryBlockEditor = ({ content, onChange }) => {
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [showFolderPicker, setShowFolderPicker] = useState(false);
  const [mediaLibrary, setMediaLibrary] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Gallery content defaults
  const layout = content.layout || 'grid-3';
  const images = content.images || [];
  const linkedFolderId = content.linkedFolderId || null;
  const linkedFolderName = content.linkedFolderName || null;
  const gap = content.gap || '16';
  const imageRadius = content.imageRadius || '8';
  const showCaptions = content.showCaptions !== false;
  const enableLightbox = content.enableLightbox !== false;
  const aspectRatio = content.aspectRatio || 'auto';
  const imageAnimation = content.imageAnimation || 'none';
  const animationSpeed = content.animationSpeed || '0.3';
  const hoverEffect = content.hoverEffect || 'zoom';

  // Fetch media and folders
  const fetchMediaAndFolders = useCallback(async () => {
    setLoading(true);
    try {
      const [mediaRes, foldersRes] = await Promise.all([
        api.get('/media'),
        api.get('/media/folders')
      ]);
      setMediaLibrary(mediaRes.data);
      setFolders(foldersRes.data);
    } catch (error) {
      console.error('Failed to fetch media:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMediaAndFolders();
  }, [fetchMediaAndFolders]);

  // Fetch images from linked folder
  const fetchFolderImages = useCallback(async () => {
    if (!linkedFolderId) return;
    
    try {
      const res = await api.get(`/media?folder_id=${linkedFolderId}`);
      const folderImages = res.data.map(media => ({
        id: media.media_id,
        url: media.url,
        alt: media.filename,
        caption: ''
      }));
      onChange({ ...content, images: folderImages });
    } catch (error) {
      console.error('Failed to fetch folder images:', error);
    }
  }, [linkedFolderId, content, onChange]);

  // Refresh folder images
  const handleRefreshFolder = () => {
    if (linkedFolderId) {
      fetchFolderImages();
      toast.success('Gallery refreshed from folder');
    }
  };

  // Handle file upload directly to gallery (from input or drag/drop)
  const uploadFiles = async (files) => {
    if (files.length === 0) return;

    // Filter to only image files
    const imageFiles = Array.from(files).filter(file => file.type.startsWith('image/'));
    if (imageFiles.length === 0) {
      toast.error('Please drop image files only');
      return;
    }

    setUploadingFiles(true);
    const newImages = [...images];

    try {
      for (const file of imageFiles) {
        const formData = new FormData();
        formData.append('file', file);
        
        // Upload to linked folder if one is set, otherwise root
        const uploadUrl = linkedFolderId 
          ? `/media/upload?folder_id=${linkedFolderId}` 
          : '/media/upload';
        
        const res = await api.post(uploadUrl, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        newImages.push({
          id: res.data.media_id,
          url: res.data.url,
          alt: file.name,
          caption: ''
        });
      }

      onChange({ ...content, images: newImages });
      toast.success(`${imageFiles.length} image(s) uploaded`);
      fetchMediaAndFolders(); // Refresh media library
    } catch (error) {
      toast.error('Failed to upload images');
    } finally {
      setUploadingFiles(false);
    }
  };

  // Handle file input change
  const handleFileUpload = async (e) => {
    await uploadFiles(e.target.files);
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if we're leaving the drop zone entirely
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    await uploadFiles(files);
  };

  // Add images from media library
  const handleSelectFromLibrary = (selectedMedia) => {
    const newImages = [...images];
    selectedMedia.forEach(media => {
      if (!newImages.find(img => img.id === media.media_id)) {
        newImages.push({
          id: media.media_id,
          url: media.url,
          alt: media.filename,
          caption: ''
        });
      }
    });
    onChange({ ...content, images: newImages });
    setShowMediaPicker(false);
  };

  // Link to a folder
  const handleLinkFolder = async (folder) => {
    try {
      const res = await api.get(`/media?folder_id=${folder.folder_id}`);
      const folderImages = res.data.map(media => ({
        id: media.media_id,
        url: media.url,
        alt: media.filename,
        caption: ''
      }));
      
      onChange({ 
        ...content, 
        linkedFolderId: folder.folder_id,
        linkedFolderName: folder.name,
        images: folderImages 
      });
      
      setShowFolderPicker(false);
      toast.success(`Linked to folder "${folder.name}"`);
    } catch (error) {
      toast.error('Failed to link folder');
    }
  };

  // Unlink folder
  const handleUnlinkFolder = () => {
    onChange({ 
      ...content, 
      linkedFolderId: null, 
      linkedFolderName: null 
    });
    toast.info('Folder unlinked');
  };

  // Remove a single image
  const handleRemoveImage = (imageId) => {
    onChange({ 
      ...content, 
      images: images.filter(img => img.id !== imageId) 
    });
  };

  // Update image caption
  const handleUpdateCaption = (imageId, caption) => {
    onChange({
      ...content,
      images: images.map(img => 
        img.id === imageId ? { ...img, caption } : img
      )
    });
  };

  // Reorder images (simple swap for now)
  const moveImage = (fromIndex, toIndex) => {
    const newImages = [...images];
    const [removed] = newImages.splice(fromIndex, 1);
    newImages.splice(toIndex, 0, removed);
    onChange({ ...content, images: newImages });
  };

  return (
    <div className="space-y-4">
      {/* Layout Selection */}
      <div>
        <Label className="text-xs text-slate-500 mb-2 block">Gallery Layout</Label>
        <div className="grid grid-cols-3 gap-2">
          {GALLERY_LAYOUTS.map(l => (
            <button
              key={l.id}
              onClick={() => onChange({ ...content, layout: l.id })}
              className={`p-3 rounded-lg border-2 text-center transition-all ${
                layout === l.id 
                  ? 'border-indigo-500 bg-indigo-50' 
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <l.icon size={20} className="mx-auto mb-1 text-slate-600" />
              <span className="text-xs font-medium">{l.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Gallery Settings */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-slate-500">Gap</Label>
          <Select value={gap} onValueChange={(v) => onChange({ ...content, gap: v })}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="4">Tight (4px)</SelectItem>
              <SelectItem value="8">Small (8px)</SelectItem>
              <SelectItem value="16">Medium (16px)</SelectItem>
              <SelectItem value="24">Large (24px)</SelectItem>
              <SelectItem value="32">Extra Large (32px)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-500">Image Corners</Label>
          <Select value={imageRadius} onValueChange={(v) => onChange({ ...content, imageRadius: v })}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Sharp</SelectItem>
              <SelectItem value="4">Subtle</SelectItem>
              <SelectItem value="8">Rounded</SelectItem>
              <SelectItem value="16">More Rounded</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-500">Aspect Ratio</Label>
          <Select value={aspectRatio} onValueChange={(v) => onChange({ ...content, aspectRatio: v })}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Auto</SelectItem>
              <SelectItem value="1/1">Square (1:1)</SelectItem>
              <SelectItem value="4/3">Standard (4:3)</SelectItem>
              <SelectItem value="16/9">Widescreen (16:9)</SelectItem>
              <SelectItem value="3/4">Portrait (3:4)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-between pt-5">
          <Label className="text-xs text-slate-500">Click to Enlarge</Label>
          <Switch 
            checked={enableLightbox} 
            onCheckedChange={(v) => onChange({ ...content, enableLightbox: v })} 
          />
        </div>
      </div>

      {/* Image Animation Settings */}
      <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
        <Label className="text-xs text-purple-700 font-medium mb-2 block">Image Animation & Effects</Label>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-500">Entrance Animation</Label>
            <Select value={imageAnimation} onValueChange={(v) => onChange({ ...content, imageAnimation: v })}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="fadeIn">Fade In</SelectItem>
                <SelectItem value="fadeInUp">Fade In Up</SelectItem>
                <SelectItem value="zoomIn">Zoom In</SelectItem>
                <SelectItem value="slideIn">Slide In</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Animation Speed</Label>
            <Select value={animationSpeed} onValueChange={(v) => onChange({ ...content, animationSpeed: v })}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0.2">Fast (0.2s)</SelectItem>
                <SelectItem value="0.3">Normal (0.3s)</SelectItem>
                <SelectItem value="0.5">Slow (0.5s)</SelectItem>
                <SelectItem value="0.8">Very Slow (0.8s)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Hover Effect</Label>
            <Select value={hoverEffect} onValueChange={(v) => onChange({ ...content, hoverEffect: v })}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="zoom">Zoom</SelectItem>
                <SelectItem value="brighten">Brighten</SelectItem>
                <SelectItem value="darken">Darken</SelectItem>
                <SelectItem value="grayscale">Grayscale → Color</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between pt-5">
            <Label className="text-xs text-slate-500">Stagger</Label>
            <Switch 
              checked={content.staggerAnimation !== false} 
              onCheckedChange={(v) => onChange({ ...content, staggerAnimation: v })} 
            />
          </div>
        </div>
      </div>

      {/* Featured Layout Options */}
      {layout === 'featured' && (
        <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
          <Label className="text-xs text-amber-700 font-medium mb-2 block">Featured Layout Options</Label>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => onChange({ ...content, thumbnailPosition: 'side' })}
              className={`p-2 rounded border text-xs flex items-center justify-center gap-2 transition-all ${
                (content.thumbnailPosition || 'side') === 'side'
                  ? 'border-amber-500 bg-amber-100 text-amber-700'
                  : 'border-slate-200 hover:border-amber-300'
              }`}
            >
              <div className="flex gap-1">
                <div className="w-6 h-4 bg-amber-400 rounded-sm"></div>
                <div className="flex flex-col gap-0.5">
                  <div className="w-2 h-1 bg-amber-300 rounded-sm"></div>
                  <div className="w-2 h-1 bg-amber-300 rounded-sm"></div>
                  <div className="w-2 h-1 bg-amber-300 rounded-sm"></div>
                </div>
              </div>
              <span>Side</span>
            </button>
            <button
              onClick={() => onChange({ ...content, thumbnailPosition: 'bottom' })}
              className={`p-2 rounded border text-xs flex items-center justify-center gap-2 transition-all ${
                content.thumbnailPosition === 'bottom'
                  ? 'border-amber-500 bg-amber-100 text-amber-700'
                  : 'border-slate-200 hover:border-amber-300'
              }`}
            >
              <div className="flex flex-col gap-1">
                <div className="w-8 h-3 bg-amber-400 rounded-sm"></div>
                <div className="flex gap-0.5">
                  <div className="w-2 h-1 bg-amber-300 rounded-sm"></div>
                  <div className="w-2 h-1 bg-amber-300 rounded-sm"></div>
                  <div className="w-2 h-1 bg-amber-300 rounded-sm"></div>
                  <div className="w-2 h-1 bg-amber-300 rounded-sm"></div>
                </div>
              </div>
              <span>Bottom</span>
            </button>
          </div>
          <p className="text-[10px] text-amber-600 mt-2">Click thumbnails to change the main image</p>
        </div>
      )}

      {/* Folder Link */}
      {linkedFolderId ? (
        <div className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg border border-indigo-200">
          <div className="flex items-center gap-2">
            <FolderOpen size={18} className="text-indigo-600" />
            <span className="text-sm font-medium text-indigo-700">
              Linked to: {linkedFolderName}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={handleRefreshFolder} title="Refresh from folder">
              <RefreshCw size={14} />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleUnlinkFolder} className="text-red-500">
              <X size={14} />
            </Button>
          </div>
        </div>
      ) : (
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => setShowFolderPicker(true)}
          className="w-full"
        >
          <FolderOpen size={14} className="mr-2" />
          Link to Media Folder
        </Button>
      )}

      {/* Image Actions */}
      <div className="flex gap-2">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => setShowMediaPicker(true)}
          className="flex-1"
        >
          <Images size={14} className="mr-2" />
          Select from Library
        </Button>
        <label className="flex-1">
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full"
            disabled={uploadingFiles}
            asChild
          >
            <span>
              <Upload size={14} className="mr-2" />
              {uploadingFiles ? 'Uploading...' : 'Upload Images'}
            </span>
          </Button>
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileUpload}
            className="hidden"
          />
        </label>
      </div>

      {/* Image Grid with Drag & Drop */}
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className="relative"
      >
        <Label className="text-xs text-slate-500 mb-2 block">
          Gallery Images ({images.length}) - Drag & drop images here
        </Label>
        
        {/* Drag overlay */}
        {isDragging && (
          <div className="absolute inset-0 bg-indigo-500/20 border-2 border-dashed border-indigo-500 rounded-lg z-10 flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-lg px-6 py-4 text-center">
              <Upload size={32} className="mx-auto mb-2 text-indigo-600" />
              <p className="font-medium text-indigo-600">Drop images here</p>
              <p className="text-xs text-slate-500">Images will be added to your gallery</p>
            </div>
          </div>
        )}
        
        {/* Uploading overlay */}
        {uploadingFiles && (
          <div className="absolute inset-0 bg-white/80 rounded-lg z-10 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full mx-auto mb-2"></div>
              <p className="text-sm font-medium text-indigo-600">Uploading images...</p>
            </div>
          </div>
        )}
        
        {images.length === 0 ? (
          <div className={`p-8 border-2 border-dashed rounded-lg text-center transition-colors ${
            isDragging ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 text-slate-400'
          }`}>
            <Upload size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm font-medium">Drag & drop images here</p>
            <p className="text-xs mt-1">or use the buttons above to add images</p>
          </div>
        ) : (
          <div className={`grid grid-cols-4 gap-2 max-h-64 overflow-y-auto p-2 rounded-lg transition-colors ${
            isDragging ? 'bg-indigo-50 ring-2 ring-indigo-500' : 'bg-slate-50'
          }`}>
            {images.map((img, idx) => (
              <div 
                key={img.id} 
                className="relative group aspect-square bg-white rounded overflow-hidden border border-slate-200"
              >
                <img 
                  src={img.url} 
                  alt={img.alt} 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-white hover:bg-white/20"
                    onClick={() => handleRemoveImage(img.id)}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
                <span className="absolute bottom-1 left-1 bg-black/60 text-white text-[10px] px-1 rounded">
                  {idx + 1}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Media Picker Dialog */}
      <Dialog open={showMediaPicker} onOpenChange={setShowMediaPicker}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Select Images from Library</DialogTitle>
          </DialogHeader>
          <MediaPickerGrid 
            media={mediaLibrary} 
            selectedIds={images.map(img => img.id)}
            onSelect={handleSelectFromLibrary}
            onClose={() => setShowMediaPicker(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Folder Picker Dialog */}
      <Dialog open={showFolderPicker} onOpenChange={setShowFolderPicker}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Select a Folder</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {folders.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">
                No folders yet. Create folders in the Media Library.
              </p>
            ) : (
              folders.map(folder => (
                <button
                  key={folder.folder_id}
                  onClick={() => handleLinkFolder(folder)}
                  className="w-full flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-all text-left"
                >
                  <FolderOpen size={20} className="text-amber-500" />
                  <span className="font-medium">{folder.name}</span>
                </button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Sub-component for media picker grid with multi-select
const MediaPickerGrid = ({ media, selectedIds, onSelect, onClose }) => {
  const [selected, setSelected] = useState(new Set(selectedIds));

  const toggleSelect = (mediaId) => {
    const newSelected = new Set(selected);
    if (newSelected.has(mediaId)) {
      newSelected.delete(mediaId);
    } else {
      newSelected.add(mediaId);
    }
    setSelected(newSelected);
  };

  const handleConfirm = () => {
    const selectedMedia = media.filter(m => selected.has(m.media_id));
    onSelect(selectedMedia);
  };

  const imageMedia = media.filter(m => m.content_type?.startsWith('image/'));

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <div className="flex-1 overflow-y-auto p-2">
        {imageMedia.length === 0 ? (
          <p className="text-center text-slate-500 py-8">No images in library</p>
        ) : (
          <div className="grid grid-cols-5 gap-2">
            {imageMedia.map(m => (
              <button
                key={m.media_id}
                onClick={() => toggleSelect(m.media_id)}
                className={`relative aspect-square rounded overflow-hidden border-2 transition-all ${
                  selected.has(m.media_id) 
                    ? 'border-indigo-500 ring-2 ring-indigo-200' 
                    : 'border-transparent hover:border-slate-300'
                }`}
              >
                <img src={m.url} alt={m.filename} className="w-full h-full object-cover" />
                {selected.has(m.media_id) && (
                  <div className="absolute top-1 right-1 w-5 h-5 bg-indigo-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs">✓</span>
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex justify-between items-center pt-3 border-t">
        <span className="text-sm text-slate-500">{selected.size} selected</span>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleConfirm} disabled={selected.size === 0}>
            Add Selected
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GalleryBlockEditor;
